import { Box, Button, Flex } from '@chakra-ui/react'
import { useApp } from '@hb/react'
import { colors, getV2PregnancyTabs } from '@hb/shared'
import { useMemo, useRef } from 'react'
import { PregnancyChartingView } from '../../../../../../charting/Pregnancy/PregnancyChartingView'
import { useProfile } from '../../../../../../contexts/user/ProfileContext'
import { useProfileLayout } from '../../../../../../contexts/user/ProfileLayoutContext'
import { Size, useResizeObserver } from '../../../../../../hooks/useResizeObserver'
import { AssessmentModal } from '../../../../../Assessments/AssessmentModal/AssessmentModal'
import { PatientPlansView, PregnancyPlansView } from '../../../../../Assessments/InsurancePlansView'
import { QuestionnairesPanel } from '../../../../../Assessments/QuestionnairesPanel'
import { AssessmentAuthorizationsTab } from '../../../Authorizations/AssessmentAuthorizations'
import { ProfileConsentForms } from '../../../ConsentForms/ProfileConsentForms'
import { AssessmentAndPatientFiles } from '../../../Files/AssessmentFiles'
import { GynProfileView } from '../../../Gyn/GynProfileView'
import { ProfileLabsView } from '../../../Labs/LabsView'
import { AssessmentLog } from '../../../Log/AssessmentLog'
import { Resizable } from '../../../Log/Resizable'
import { AssessmentPaymentsTab } from '../../../Payments'
import { AssessmentHeaderRow } from '../../V1/AssessmentHeader'
import { PatientControlPanel } from '../Patient/PatientControlPanel'
import { PregnancyControlPanel } from '../Pregnancy/PregnancyControlPanel'

const PregnancyContentBody = ({ width }: { width: number }) => {
  const { tab } = useProfile()
  const tabPath = tab?.path

  switch (tabPath) {
    case 'plans':
      return (
        <Box borderRadius={4} bg="white" w="100%" m={2}>
          <PregnancyPlansView />
        </Box>
      )
    case 'payments':
      return <AssessmentPaymentsTab />
    case 'files':
      return (
        <Box w="100%" p={2}>
          <AssessmentAndPatientFiles width={width} />
        </Box>
      )
    case 'sign-on':
      return (
        <Box w="100%" p={3}>
          <QuestionnairesPanel mobileLayout={width < 600} width={width} />
        </Box>
      )
    case 'authorizations':
      return <AssessmentAuthorizationsTab />
    case 'charting':
      return <PregnancyChartingView />
    case 'labs':
      return <ProfileLabsView />
    default:
      return <PregnancyControlPanel />
  }
}

const PregnancyContent = ({ size }: { size: Size }) => {
  const { selectedAssessment, user, goTo: onTabSelect, tab } = useProfile()
  const { layoutMode, focusMode } = useProfileLayout()
  const { appName } = useApp()
  const displayedTabs = useMemo(
    () => getV2PregnancyTabs(appName).filter(t => t.path !== 'patient'),
    [appName],
  )
  const bodyRef = useRef<HTMLDivElement>(null)

  const { width, height } = useResizeObserver(bodyRef, 'content')
  return (
    <Flex w="100%" h="100%" flexFlow="column">
      {/* Hidden while an encounter form is open so the form gets the full height */}
      {focusMode ? null : (
        <>
          <AssessmentHeaderRow width={size.width} />
          <Flex
            gap={2}
            py={1}
            px={2}
            border="1px solid #cdcdcd"
            bg="gray.100"
            w="100%"
            overflowX="auto"
            sx={{ scrollbarWidth: 'thin' }}>
            {displayedTabs.map((t, i) => (
              <Button
                bg={tab?.path === t.path ? colors.pink.hex : 'white'}
                color={tab?.path === t.path ? 'blackAlpha.700' : 'blackAlpha.500'}
                border="1px solid #cdcdcd"
                borderRadius="full"
                size="sm"
                key={i}
                px={3}
                flexShrink={0}
                whiteSpace="nowrap"
                onClick={() =>
                  onTabSelect({
                    tab: t.path,
                    entity: { id: selectedAssessment.id, type: 'pregnancy' },
                  })
                }
                cursor="pointer">
                {t.name}
              </Button>
            ))}
          </Flex>
        </>
      )}
      <Flex ref={bodyRef} flex={1} w="100%" minH="0">
        {appName === 'providers-app' || layoutMode !== 'desktop' ? (
          <Box bg="#efefef" w="100%" h="100%" overflowY="auto">
            <PregnancyContentBody width={width} />
          </Box>
        ) : (
          <Resizable
            width={width}
            height={height}
            localStorageId="user-profile-resize"
            leftChild={width => (
              <Box bg="#efefef" w="100%" h="100%" overflowY="auto">
                <PregnancyContentBody width={width} />
              </Box>
            )}
            rightChild={() => <AssessmentLog maxHeight={size.height} />}
          />
        )}
      </Flex>
      {user && selectedAssessment.populated && tab?.path === 'assessment' ? (
        <AssessmentModal
          assessment={selectedAssessment.populated}
          id={selectedAssessment.id}
          isOpen
          onClose={() =>
            onTabSelect({ entity: { id: selectedAssessment.id, type: 'pregnancy' }, tab: null })
          }
          // user={user}
        />
      ) : null}
    </Flex>
  )
}

const PatientContent = () => {
  const { subTab } = useProfile()
  // if (!subTab || !isPatientTab(subTab)) return <PatientControlPanel />
  switch (subTab?.path) {
    case 'consent-forms':
      return <ProfileConsentForms />
    case 'insurance':
      return <PatientPlansView />
    default:
      return <PatientControlPanel />
  }
}

export const BodyContent = ({ size }: { size: Size }) => {
  const { tab, assessmentId, selectedItem } = useProfile()

  const selectedItemType = useMemo(() => selectedItem?.type || 'patient', [selectedItem])

  if (selectedItemType === 'pregnancy' && assessmentId && tab?.path !== 'patient')
    return <PregnancyContent size={size} />
  if (tab?.path === 'gyn') return <GynProfileView />
  return <PatientContent />
}
